import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GrMoney } from "react-icons/gr";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { TrendingUp, Users, MapPin, Award, Activity, User } from "lucide-react";
import DataContext from "../Context/DAtaContext";
import UsersContext from "../Context/UsersContext";
import InvestmentByGender from "./InvestmentByGender";

function InvestmentStatistics() {
  const { Zones, Payment, Villages } = useContext(DataContext);
  const { users, farmers } = useContext(UsersContext);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [animatedValues, setAnimatedValues] = useState({});

  // Get agents who have registered at least one farmer
  const activeAgents = users?.filter((agent) =>
    farmers?.some((farmer) => farmer?.creator_id === agent?.id)
  );

  // Function to calculate average farmer payments
  const averageInvestment = Payment?.length
    ? Payment?.reduce((sum, payment) => sum + (payment.amount || 0), 0) /
      Payment.length
    : 0;

  // Calculate agents with their total payments
  const AgentsWithPayments =
    users
      ?.map((agent) => {
        const agentPayments = Payment?.filter(
          (pay) => pay?.officer_id === agent?.id
        );

        if (agentPayments?.length > 0) {
          const totalCollected = agentPayments.reduce(
            (sum, pay) => sum + (pay?.amount || 0),
            0
          );

          return {
            ...agent,
            totalCollected,
          };
        }

        return {
          ...agent,
          totalCollected: 0,
        };
      })
      .filter((agent) => agent !== null) || [];

  // Calculate collection per zone
  const CollectionPerZone =
    Zones?.map((zone) => {
      const zoneVillages = Villages?.filter(
        (village) => village?.zone_id === zone?.id
      );

      const villageIds = zoneVillages?.map((v) => v.id) || [];

      const agentsInZone = AgentsWithPayments?.filter((agent) =>
        villageIds.includes(agent?.village_id)
      );

      const totalZoneCollection = agentsInZone?.reduce(
        (sum, agent) => sum + (agent?.totalCollected || 0),
        0
      );

      return {
        zone: zone?.name,
        totalCollected: totalZoneCollection,
        id: zone?.id,
        agentCount: agentsInZone?.length || 0,
      };
    }) || [];

  // Calculate summary statistics
  const totalCollection = AgentsWithPayments.reduce(
    (sum, agent) => sum + (agent.totalCollected || 0),
    0
  );
  const totalOfficers = AgentsWithPayments.length;
  const activeZones = CollectionPerZone.filter(
    (zone) => zone.totalCollected > 0
  ).length;
  const topPerformer =
    AgentsWithPayments.length > 0
      ? AgentsWithPayments.reduce((max, agent) =>
          (agent.totalCollected || 0) > (max.totalCollected || 0) ? agent : max
        )
      : null;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // Animate numbers
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalCollection,
        totalOfficers,
        activeZones,
        topPerformerAmount: topPerformer?.totalCollected || 0,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    totalCollection,
    totalOfficers,
    activeZones,
    topPerformer?.totalCollected,
  ]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // 1. Investment per Gender per Zone
  const genderInvestmentPerZone = Zones?.map((zone) => {
    const villageIds = Villages?.filter((v) => v.zone_id === zone.id).map(
      (v) => v.id
    );

    const farmersInZone = farmers?.filter((f) =>
      villageIds.includes(f.village_id)
    );

    const genderTotals = {};
    let total_of_males = 0;
    let total_of_females = 0;

    farmersInZone?.forEach((farmer) => {
      const payments = Payment?.filter((p) => p.farmer_id === farmer.uuid);
      const total = payments?.reduce((sum, p) => sum + (p.amount || 0), 0);
      const gender = farmer.gender || "Unknown";

      // Add to investment totals
      if (!genderTotals[gender]) genderTotals[gender] = 0;
      genderTotals[gender] += total;

      // Add to gender counts
      if (gender.toLowerCase() === "male") {
        total_of_males += 1;
      } else if (gender.toLowerCase() === "female") {
        total_of_females += 1;
      }
    });

    return {
      zone: zone.name,
      total_of_males,
      total_of_females,
      ...genderTotals,
    };
  });

  const getTotalInvestment = (zone) => {
    const female = zone.female || 0;
    const male = zone.male || 0;
    return female + male;
  };

  const getTotalBeneficiaries = (zone) => {
    return zone.total_of_males + zone.total_of_females;
  };

  const getFemalePercentage = (zone) => {
    const total = getTotalBeneficiaries(zone);
    return total > 0 ? ((zone.total_of_females / total) * 100).toFixed(1) : 0;
  };

  const getInvestmentPercentage = (zone) => {
    const total = getTotalInvestment(zone);
    const female = zone.female || 0;
    return total > 0 ? ((female / total) * 100).toFixed(1) : 0;
  };

  const StatCards = ({ title, value, icon: Icon, color, trend, subtitle }) => (
    <motion.div
      variants={itemVariants}
      className="p-6 transition-shadow bg-white border border-gray-200 shadow-lg dark:bg-gray-800 rounded-xl dark:border-gray-700 hover:shadow-xl"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <motion.p
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            className="mt-2 text-2xl font-bold text-gray-900 dark:text-white"
          >
            {value}
          </motion.p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          )}
          {trend && (
            <p className="flex items-center mt-1 text-sm text-green-600 dark:text-green-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              {trend}
            </p>
          )}
        </div>
        <div
          className={`p-3 md:hidden xl:block dark:bg-gray-900/60 rounded-lg ${color}`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const renderContent = () => {
    switch (selectedTab) {
      case "overview":
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <StatCards
                title="Total Collection"
                value={formatCurrency(animatedValues.totalCollection || 0)}
                icon={GrMoney}
                color="bg-green-500"
                trend={`${formatNumber(
                  animatedValues.totalCollection || 0
                )} TZS`}
              />
              <StatCards
                title="Average Investment"
                value={formatCurrency(averageInvestment?.toFixed(0) || 0)}
                icon={GrMoney}
                color="bg-green-500"
                trend={`${formatNumber(
                  animatedValues.totalCollection || 0
                )} TZS`}
              />
              <StatCards
                title="Top Performer"
                value={formatCurrency(animatedValues.topPerformerAmount || 0)}
                icon={Award}
                color="bg-green-500"
                subtitle={topPerformer?.name || "No data"}
              />
              <StatCards
                title="Village Agents"
                value={animatedValues.totalOfficers || 0}
                icon={Users}
                color="bg-green-500"
                subtitle="Total Village agents"
              />
              <StatCards
                title="Active Agents"
                value={activeAgents?.length || 0}
                icon={Users}
                color="bg-green-500"
                subtitle="Active Village Agents"
              />
              <StatCards
                title="Active Zones"
                value={`${animatedValues.activeZones || 0}/${
                  Zones?.length || 0
                }`}
                icon={MapPin}
                color="bg-green-500"
                trend={`${Math.round(
                  ((animatedValues.activeZones || 0) / (Zones?.length || 1)) *
                    100
                )}% coverage`}
              />
            </div>

            {/* Quick Overview Chart */}
            <div className="bg-white shadow-lg dark:bg-gray-800 rounded-xl">
              {/* gender investments demographics */}
              <InvestmentByGender />
            </div>
          </motion.div>
        );

      case "zones":
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* zone gender demographics */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {genderInvestmentPerZone?.map((zone, index) => {
                const totalInvestment = getTotalInvestment(zone);
                const totalBeneficiaries = getTotalBeneficiaries(zone);
                const femalePercentage = getFemalePercentage(zone);
                const investmentPercentage = getInvestmentPercentage(zone);

                return (
                  <div
                    key={index}
                    className="p-6 text-gray-800 bg-white border border-gray-200 shadow dark:bg-gray-800 rounded-xl dark:border-gray-700 dark:text-gray-300"
                  >
                    {/* Zone Name */}
                    <div className="flex items-center mb-4 space-x-2">
                      <MapPin className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                      <h3 className="text-lg font-semibold">{zone.zone}</h3>
                    </div>

                    {/* Investment */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2">
                        <GrMoney className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Total Investment</span>
                      </div>
                      <p className="text-lg font-bold text-gray-700 text-end dark:text-green-400">
                        {totalInvestment > 0
                          ? formatCurrency(totalInvestment)
                          : "No Data"}
                      </p>
                    </div>

                    {/* Beneficiaries */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-800 dark:text-gray-400 " />
                        <span className="text-sm">Total Investors</span>
                      </div>
                      <p className="text-lg font-bold text-gray-800 text-end dark:text-gray-400">
                        {totalBeneficiaries}
                      </p>
                      {totalBeneficiaries > 0 && (
                        <div className="mt-2">
                          <div className="flex justify-between mb-1 text-xs">
                            <span className="text-gray-800 dark:text-gray-400">
                              Female: {femalePercentage}%
                            </span>
                            <span className="text-gray-800 dark:text-gray-400">
                              Male: {(100 - femalePercentage).toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-2 bg-green-200 rounded-full dark:bg-green-700">
                            <div
                              className="h-full bg-yellow-500 dark:bg-yellow-400"
                              style={{ width: `${femalePercentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Gender Investment Breakdown */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="p-3 border border-gray-200 rounded-lg dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                        <div className="flex items-center mb-1 space-x-2 text-xs">
                          <User className="w-4 h-4 text-green-600 " />
                          <span>Female</span>
                        </div>
                        <p className="text-lg font-bold text-gray-700 text-end dark:text-gray-400">
                          {zone.total_of_females}
                        </p>
                        {zone.female && (
                          <p className="mt-1 text-xs font-bold text-gray-700 dark:text-green-400">
                            {formatCurrency(zone.female)}
                          </p>
                        )}
                      </div>

                      <div className="p-3 border border-gray-200 rounded-lg dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                        <div className="flex items-center mb-1 space-x-2 text-xs">
                          <User className="w-4 h-4 text-green-600 " />
                          <span>Male</span>
                        </div>
                        <p className="text-lg font-bold text-gray-700 text-end dark:text-gray-400">
                          {zone.total_of_males}
                        </p>
                        {zone.male && (
                          <p className="mt-1 text-xs font-bold text-gray-700 dark:text-green-400">
                            {formatCurrency(zone.male)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Investment Distribution */}
                    {totalInvestment > 0 && (
                      <div>
                        <div className="flex items-center mb-1 space-x-2 text-sm">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          <span>Investment Distribution</span>
                        </div>
                        <div className="flex justify-between mb-1 text-xs">
                          <span>Female: {investmentPercentage}%</span>
                          <span>
                            Male: {(100 - investmentPercentage).toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700">
                          <div
                            className="h-full bg-gradient-to-r from-green-600 to-cyan-500"
                            style={{ width: `${investmentPercentage}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* No Data Case */}
                    {totalBeneficiaries === 0 && (
                      <p className="mt-4 text-sm text-gray-500">
                        No beneficiary data available
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Zone Performance Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-xl">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Zone Performance
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={CollectionPerZone}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="zone" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar
                      dataKey="totalCollected"
                      fill="#10B981"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="p-6 bg-white shadow-lg dark:bg-gray-800 rounded-xl">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Zone Distribution
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={CollectionPerZone}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="zone" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Area
                      type="monotone"
                      dataKey="totalCollected"
                      stroke="#10B981"
                      fill="#10B981"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        );

      case "officers":
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {AgentsWithPayments.sort(
                (a, b) => (b.totalCollected || 0) - (a.totalCollected || 0)
              ).map((officer, index) => (
                <motion.div
                  key={officer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 bg-white border border-gray-200 shadow-lg dark:bg-gray-800 rounded-xl dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-10 h-10 mr-3 rounded-full bg-gradient-to-br from-green-500 to-cyan-500">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {officer.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          #{index + 1} Performer
                        </p>
                      </div>
                    </div>
                    {index < 3 && (
                      <div
                        className={`px-2 py-1 rounded-full  text-xs font-medium ${
                          index === 0
                            ? "bg-yellow-100 dark:bg-gray-900/60 text-yellow-800"
                            : index === 1
                            ? "bg-gray-100 dark:bg-gray-900/60 text-gray-800"
                            : "bg-orange-100 dark:bg-gray-900/60 text-orange-800"
                        }`}
                      >
                        {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                      </div>
                    )}
                  </div>
                  <p className="text-xl font-bold text-gray-900 dark:text-green-400">
                    {formatCurrency(officer.totalCollected || 0)}
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {officer.email}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className=" bg-gray-50 dark:bg-gray-800">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
            Investment
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Field officers collection statistics and performance metrics
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex p-1 mb-8 space-x-1 bg-gray-200 rounded-lg dark:bg-gray-700"
        >
          {[
            { id: "overview", label: "Overview", icon: Activity },
            { id: "zones", label: "Zones", icon: MapPin },
            { id: "officers", label: "Officers", icon: Users },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedTab(id)}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
                selectedTab === id
                  ? "bg-green-600 dark:bg-gray-800 text-white dark:text-gray-200 shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </button>
          ))}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default InvestmentStatistics;

// components
